/**
 * Katalog (Spec 01): EIN Katalog je Mandant. Speisen/Getraenke/Equipment/Personal in einer
 * Item-Liste (kind). Kind-Filter-Chips + Kategorie-Filter + Suche; Klick auf eine Zeile oeffnet
 * den Bearbeiten-Drawer. "Position anlegen" und "Kategorie" als eigene Drawer.
 *
 * Geld IMMER Integer-Cents (Konvention aus packages/db): das Preisfeld nimmt Euro entgegen und
 * rechnet beim Speichern verlustfrei in Cent (Math.round). Archivieren ist Soft-Delete
 * (archived_at), hartes Loeschen bleibt Owner/Admin vorbehalten (Backend-Rollen-Gate).
 */
import { Fragment, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { CatalogCategory, CatalogItem, CatalogKind, MenuCard, PricingUnit, VatClass } from "../ui/types";
import { money, parseGermanEurosToCents } from "../ui/format";
// LMIV-Hauptallergene + Diät-Flags: EINE gemeinsame Quelle für alle Flächen (ui/lmiv).
import { LMIV, DIETARY } from "../ui/lmiv";
import {
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  LoadingRows,
  PageHeader,
} from "../ui/components";

/* --------------------------------- Vokabular ------------------------------- */
const KIND_LABEL: Record<CatalogKind, string> = {
  food: "Speise",
  drink: "Getränk",
  equipment: "Equipment",
  staff: "Personal",
  other: "Sonstiges",
};
const KIND_ICON: Record<CatalogKind, string> = {
  food: "restaurant",
  drink: "local_bar",
  equipment: "chair",
  staff: "badge",
  other: "category",
};
const PRICING_UNIT_LABEL: Record<PricingUnit, string> = {
  per_person: "pro Person",
  per_piece: "pro Stück",
  per_hour: "pro Stunde",
  flat: "pauschal",
};
const VAT_LABEL: Record<VatClass, string> = {
  food: "Speise (7 %)",
  drink: "Getränk (19 %)",
  service: "Dienstleistung (19 %)",
};

const KIND_ORDER: CatalogKind[] = ["food", "drink", "equipment", "staff", "other"];

/** L10: deutsches Komma auch in der Vorbefuellung (Parser akzeptiert weiterhin beides). */
function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

/**
 * L1: Sanitisiert die Preis-Eingabe waehrend des Tippens — nur Ziffern, Komma und Punkt
 * (Punkt = Tausender, Komma = Dezimal). Verhindert Freitext wie „12,50abc". Ein zweites Komma
 * wird verworfen, damit hoechstens ein Dezimaltrenner bleibt. Das eigentliche Parsen/Runden
 * passiert weiterhin beim Speichern via parseGermanEurosToCents.
 */
function sanitizePriceInput(value: string): string {
  let out = value.replace(/[^\d.,]/g, "");
  const firstComma = out.indexOf(",");
  if (firstComma !== -1) {
    out = out.slice(0, firstComma + 1) + out.slice(firstComma + 1).replace(/,/g, "");
  }
  return out;
}

/** Form-State fuer Anlegen/Bearbeiten einer Position. */
interface ItemForm {
  id: string | null;
  name: string;
  kind: CatalogKind;
  categoryId: string;
  priceEuros: string;
  pricingUnit: PricingUnit;
  vatClass: VatClass;
  description: string;
  priceNote: string;
  // M-K: Tri-State — `null` = ungepflegt (LMIV-relevant, nie still zu „geprüft: keine" [] machen).
  allergens: string[] | null;
  dietary: Record<string, boolean>;
  // Mehrsprachigkeit: { "<lang>": { name, description } } — im Drawer je Sprache pflegbar.
  translations: Record<string, { name?: string; description?: string | null }>;
  archived: boolean;
}

function emptyForm(kind: CatalogKind = "food"): ItemForm {
  return {
    id: null,
    name: "",
    kind,
    categoryId: "",
    priceEuros: "",
    pricingUnit: kind === "staff" ? "per_hour" : "per_person",
    vatClass: kind === "drink" ? "drink" : kind === "food" ? "food" : "service",
    description: "",
    priceNote: "",
    allergens: null, // neu = ungepflegt (Tri-State), nicht „geprüft: keine"
    dietary: {},
    translations: {},
    archived: false,
  };
}

function formFromItem(it: CatalogItem): ItemForm {
  return {
    id: it.id,
    name: it.name,
    kind: it.kind,
    categoryId: it.categoryId ?? "",
    priceEuros: centsToEuros(it.priceCents),
    pricingUnit: it.pricingUnit,
    vatClass: it.vatClass,
    description: it.description ?? "",
    priceNote: it.priceNote ?? "",
    allergens: it.allergens ?? null, // `null` (ungepflegt) erhalten, nicht zu [] normalisieren
    dietary: it.dietary ?? {},
    translations: (it.translations ?? {}) as Record<string, { name?: string; description?: string | null }>,
    archived: it.archivedAt != null,
  };
}

export default function CatalogPage() {
  const navigate = useNavigate();
  const itemsQ = useApi<CatalogItem[]>("/api/catalog/items?includeArchived=true");
  const catsQ = useApi<CatalogCategory[]>("/api/catalog/categories?includeArchived=true");
  // P1 (Speisekarten): Karten fuer den Filter — Kategorien tragen cardId, Positionen haengen
  // ueber ihre Kategorie an der Karte.
  const cardsQ = useApi<MenuCard[]>("/api/menu-cards");

  const [kindFilter, setKindFilter] = useState<CatalogKind | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  // CX-Audit 2026-07-17: ?source=ai_import zeigt gezielt die KI-importierten Positionen —
  // Ziel des „Fertig"-Schritts im Speisekarten-Import (vorher landete man unfokussiert in
  // der Gesamtliste). Abwählbar über einen sichtbaren Filter-Chip.
  const [params, setParams] = useSearchParams();
  const sourceFilter = params.get("source");
  // Pillar-Einstieg (UX 19.07.2026): /katalog zeigt ZUERST die Speisekarten als Kacheln —
  // der Mensch waehlt eine Karte, dann kommt die Liste. Der Karten-Filter lebt deshalb in
  // der URL (?cardId=<uuid|none>), ?view=liste ist die bewusste Gesamtliste; Deep-Links
  // (?source=ai_import) springen weiterhin direkt in die Liste.
  const cardFilter = params.get("cardId") ?? "all";
  const setCardFilter = (value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete("cardId");
          next.set("view", "liste");
        } else {
          next.set("cardId", value);
          next.delete("view");
        }
        return next;
      },
      { replace: true },
    );
  const backToPillar = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("cardId");
        next.delete("view");
        next.delete("source");
        return next;
      },
      { replace: true },
    );
  const clearSourceFilter = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("source");
        return next;
      },
      { replace: true },
    );

  const [itemForm, setItemForm] = useState<ItemForm | null>(null);
  // Mehrsprachigkeit: aktuell bearbeitete Übersetzungs-Sprache im Positions-Drawer.
  const [editLang, setEditLang] = useState("en");
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const items = itemsQ.data ?? [];
  const categories = useMemo(
    () => (catsQ.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [catsQ.data],
  );
  const activeCategories = categories.filter((c) => c.archivedAt == null);
  const categoryName = (id: string | null) => (id ? categories.find((c) => c.id === id)?.name ?? "—" : "Ohne Kategorie");
  const cards = cardsQ.data ?? [];
  // Kategorie -> Karte (P1): Positionen gehoeren ueber ihre Kategorie zu einer Karte.
  const cardByCategory = useMemo(() => new Map(categories.map((c) => [c.id, c.cardId ?? null])), [categories]);
  const matchesCard = (it: CatalogItem): boolean => {
    if (cardFilter === "all") return true;
    const cardId = it.categoryId ? cardByCategory.get(it.categoryId) ?? null : null;
    return cardFilter === "none" ? cardId == null : cardId === cardFilter;
  };
  // Bei gewaehlter Karte zeigt der Kategorien-Filter nur deren Kategorien (weniger Rauschen).
  const categoryOptions = activeCategories.filter((c) =>
    cardFilter === "all" ? true : cardFilter === "none" ? (c.cardId ?? null) == null : c.cardId === cardFilter,
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((it) => (showArchived ? true : it.archivedAt == null))
      .filter((it) => (sourceFilter ? it.source === sourceFilter : true))
      .filter((it) => (kindFilter === "all" ? true : it.kind === kindFilter))
      .filter((it) => matchesCard(it))
      .filter((it) =>
        categoryFilter === "all"
          ? true
          : categoryFilter === "none"
            ? it.categoryId == null
            : it.categoryId === categoryFilter,
      )
      .filter((it) => (q ? it.name.toLowerCase().includes(q) || (it.description ?? "").toLowerCase().includes(q) : true))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, kindFilter, categoryFilter, cardFilter, cardByCategory, showArchived, sourceFilter]);

  // Sichtbare Karten-Gruppierung (UX 19.07.2026): „Alle Karten" wirkte wie ein flacher
  // Eintopf — die Speisekarte ist aber die natuerliche Gliederung des Katalogs. Die Liste
  // gruppiert deshalb nach Karte (Reihenfolge der Karten-Verwaltung), „Ohne Karte" zuletzt.
  // Ohne angelegte Karten bleibt alles wie zuvor (eine Gruppe ohne Header).
  const groupedRows = useMemo((): { key: string; label: string | null; items: CatalogItem[] }[] => {
    if (cards.length === 0) return [{ key: "all", label: null, items: filtered }];
    const byCard = new Map<string | null, CatalogItem[]>();
    for (const it of filtered) {
      const cardId = it.categoryId ? cardByCategory.get(it.categoryId) ?? null : null;
      const list = byCard.get(cardId) ?? [];
      list.push(it);
      byCard.set(cardId, list);
    }
    const groups: { key: string; label: string | null; items: CatalogItem[] }[] = [];
    for (const k of cards) {
      const inCard = byCard.get(k.id) ?? [];
      if (inCard.length > 0) groups.push({ key: k.id, label: k.name, items: inCard });
    }
    const none = byCard.get(null) ?? [];
    if (none.length > 0) groups.push({ key: "none", label: "Ohne Karte", items: none });
    return groups;
  }, [filtered, cards, cardByCategory]);

  // L2: Zaehler beziehen sich auf die GEFILTERTE Menge (Textsuche + Kategorie + Archiv-Toggle),
  // nur der Kind-Filter selbst wird ausgeklammert — jeder Chip zeigt seine eigene Kind-Zahl.
  // Sonst zeigten die Tabs „Alle (11)" obwohl die Suche nur 5 Zeilen listet.
  const kindCounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = items
      .filter((it) => (showArchived ? true : it.archivedAt == null))
      .filter((it) => matchesCard(it))
      .filter((it) =>
        categoryFilter === "all"
          ? true
          : categoryFilter === "none"
            ? it.categoryId == null
            : it.categoryId === categoryFilter,
      )
      .filter((it) => (q ? it.name.toLowerCase().includes(q) || (it.description ?? "").toLowerCase().includes(q) : true));
    const counts: Record<string, number> = { all: base.length };
    for (const k of KIND_ORDER) counts[k] = base.filter((it) => it.kind === k).length;
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, categoryFilter, cardFilter, cardByCategory, showArchived]);

  function openCreate() {
    setFormError(null);
    setItemForm(emptyForm(kindFilter === "all" ? "food" : kindFilter));
  }
  function openEdit(it: CatalogItem) {
    setFormError(null);
    setItemForm(formFromItem(it));
  }

  async function handleSaveItem(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!itemForm) return;
    // N-F2: leeres Feld = 0 (erlaubt), sonst ungültige Eingabe als Fehler melden statt still auf 0.
    const priceRaw = itemForm.priceEuros.trim();
    const priceCents = priceRaw === "" ? 0 : parseGermanEurosToCents(priceRaw);
    if (priceCents == null) {
      setFormError("Ungültiger Preis – bitte als Betrag eingeben, z. B. 12,90.");
      return;
    }
    setBusy(true);
    setFormError(null);
    const payload = {
      name: itemForm.name.trim(),
      kind: itemForm.kind,
      categoryId: itemForm.categoryId || null,
      priceCents,
      pricingUnit: itemForm.pricingUnit,
      vatClass: itemForm.vatClass,
      description: itemForm.description.trim() || null,
      priceNote: itemForm.priceNote.trim() || null,
      allergens: itemForm.allergens,
      dietary: itemForm.dietary,
      // Mehrsprachigkeit: leere Übersetzungen (kein Name) fallen beim Speichern raus.
      translations: Object.fromEntries(Object.entries(itemForm.translations).filter(([, v]) => v?.name?.trim())),
    };
    try {
      if (itemForm.id) {
        await apiMutate(`/api/catalog/items/${itemForm.id}`, "PATCH", { ...payload, archived: itemForm.archived });
      } else {
        await apiMutate("/api/catalog/items", "POST", payload);
      }
      setItemForm(null);
      itemsQ.reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchive(it: CatalogItem) {
    try {
      await apiMutate(`/api/catalog/items/${it.id}`, "PATCH", { archived: it.archivedAt == null });
      itemsQ.reload();
    } catch (err) {
      setFormError(errorText(err));
    }
  }

  async function handleCreateCategory(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = (form.get("name") as string)?.trim();
    if (!name) return;
    const kindScope = form.get("kindScope") as string;
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/catalog/categories", "POST", {
        name,
        kindScope: kindScope || null,
      });
      setCatDrawerOpen(false);
      catsQ.reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const loading = itemsQ.loading || catsQ.loading;
  const error = itemsQ.error ?? catsQ.error;

  return (
    <CatalogInner />
  );

  /* ----------------------- Pillar-Einstieg: Speisekarten ---------------------- */
  // /katalog zeigt zuerst die Karten als Kacheln (der Mensch denkt in Speisekarten, nicht in
  // 83 Einzelzeilen). Die Liste ist die Detail-Ansicht einer Karte bzw. die bewusste
  // Gesamtliste (?view=liste). Tenants ohne Karten sehen unveraendert direkt die Liste.
  function CatalogInner() {
    const wantsPillar = !sourceFilter && !params.get("cardId") && params.get("view") !== "liste";
    if (wantsPillar && (cardsQ.loading || itemsQ.loading || catsQ.loading)) {
      return (
        <>
          <PageHeader title="Katalog" subtitle="Speisen, Getränke, Equipment und Personal – die Bausteine Ihrer Angebote." />
          <LoadingRows />
        </>
      );
    }
    if (wantsPillar && cards.length > 0) {
      const activeItems = items.filter((it) => it.archivedAt == null);
      const noCardCount = activeItems.filter((it) => {
        const cardId = it.categoryId ? cardByCategory.get(it.categoryId) ?? null : null;
        return cardId == null;
      }).length;
      const CARD_KIND_META: Record<string, { icon: string; label: string }> = {
        food: { icon: "restaurant", label: "Speisekarte" },
        drinks: { icon: "local_bar", label: "Getränkekarte" },
        mixed: { icon: "restaurant_menu", label: "Gemischt" },
      };
      return (
        <>
          <PageHeader
            title="Katalog"
            subtitle="Ihre Speisekarten – wählen Sie eine Karte, um ihre Positionen zu sehen und zu pflegen."
            actions={
              <>
                <button type="button" className="btn-secondary" onClick={() => navigate("/katalog/karten")}>
                  <Icon name="menu_book" className="text-[18px]" /> Karten verwalten
                </button>
                <button type="button" className="btn-secondary" onClick={() => navigate("/katalog/pakete")}>
                  <Icon name="inventory_2" className="text-[18px]" /> Pakete
                </button>
                <button type="button" className="btn-primary" onClick={() => navigate("/katalog/import")}>
                  <Icon name="auto_awesome" className="text-[18px]" /> Speisekarte importieren
                </button>
              </>
            }
          />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((card) => {
              const meta = CARD_KIND_META[card.kind] ?? CARD_KIND_META.food!;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setCardFilter(card.id)}
                  className="card group flex flex-col gap-3 p-5 text-left transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
                      <Icon name={meta.icon} className="text-[22px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-body-lg text-body-lg font-semibold text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface">
                        {card.name}
                      </p>
                      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        {meta.label} · {card.categoryCount ?? 0} Kategorien · {card.itemCount ?? 0} Positionen
                      </p>
                    </div>
                  </div>
                  <div className="mt-auto flex items-center justify-between border-t border-outline-variant/30 pt-3 font-body-sm text-body-sm text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim">
                    <span>{card.active ? "aktiv" : "inaktiv"}</span>
                    <span className="inline-flex items-center gap-1 text-primary">
                      Öffnen <Icon name="arrow_forward" className="text-[16px]" />
                    </span>
                  </div>
                </button>
              );
            })}
            {noCardCount > 0 && (
              <button
                type="button"
                onClick={() => setCardFilter("none")}
                className="card group flex flex-col gap-3 p-5 text-left transition-shadow hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-surface-container-high text-on-surface-variant dark:bg-surface-variant/30">
                    <Icon name="category" className="text-[22px]" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-body-lg text-body-lg font-semibold text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface">
                      Ohne Karte
                    </p>
                    <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                      Equipment, Personal & Positionen ohne Karten-Zuordnung · {noCardCount}
                    </p>
                  </div>
                </div>
                <div className="mt-auto flex items-center justify-end border-t border-outline-variant/30 pt-3 font-body-sm text-body-sm text-primary dark:border-outline/30">
                  Öffnen <Icon name="arrow_forward" className="ml-1 text-[16px]" />
                </div>
              </button>
            )}
            <button
              type="button"
              onClick={() => setCardFilter("all")}
              className="card group flex flex-col items-center justify-center gap-2 border-dashed p-5 text-center transition-shadow hover:shadow-md"
            >
              <Icon name="list" className="text-[26px] text-on-surface-variant" />
              <span className="font-body-md text-body-md font-medium text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface">
                Alle Positionen als Liste
              </span>
              <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {activeItems.length} Positionen, nach Karten gruppiert
              </span>
            </button>
          </div>
        </>
      );
    }
    return renderList();
  }

  function renderList() {
  return (
    <>
      <PageHeader
        title="Katalog"
        subtitle="Speisen, Getränke, Equipment und Personal – die Bausteine Ihrer Angebote."
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => navigate("/katalog/karten")}>
              <Icon name="menu_book" className="text-[18px]" /> Karten
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate("/katalog/pakete")}>
              <Icon name="inventory_2" className="text-[18px]" /> Pakete
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate("/katalog/import")}>
              <Icon name="auto_awesome" className="text-[18px]" /> Speisekarte importieren
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCatDrawerOpen(true)}>
              <Icon name="new_label" className="text-[18px]" /> Kategorie
            </button>
            <button type="button" className="btn-primary" onClick={openCreate}>
              <Icon name="add" className="text-[18px]" /> Position anlegen
            </button>
          </>
        }
      />

      {/* Pillar-Navigation: zurueck zur Karten-Uebersicht + aktueller Kontext. */}
      {cards.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button type="button" className="inline-flex items-center gap-1.5 font-body-sm text-body-sm text-primary hover:underline" onClick={backToPillar}>
            <Icon name="arrow_back" className="text-[16px]" /> Karten-Übersicht
          </button>
          <span className="text-on-surface-variant/40">/</span>
          <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            {cardFilter === "all" ? "Alle Positionen" : cardFilter === "none" ? "Ohne Karte" : cards.find((k) => k.id === cardFilter)?.name ?? "Karte"}
          </span>
        </div>
      )}

      {/* Kind-Filter-Chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        <FilterChip active={kindFilter === "all"} onClick={() => setKindFilter("all")} label={`Alle (${kindCounts.all ?? 0})`} />
        {KIND_ORDER.map((k) => (
          <FilterChip
            key={k}
            active={kindFilter === k}
            onClick={() => setKindFilter(k)}
            icon={KIND_ICON[k]}
            label={`${KIND_LABEL[k]} (${kindCounts[k] ?? 0})`}
          />
        ))}
      </div>

      <div className="card overflow-hidden">
        {/* Toolbar: Suche + Kategorie-Filter + Archiv-Toggle */}
        <div className="flex flex-wrap items-center gap-3 border-b border-outline-variant/30 p-4 dark:border-outline/30">
          <div className="group relative min-w-[220px] flex-1 max-w-sm">
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
            />
            <input
              className="input !pl-10"
              placeholder="Suchen (Name, Beschreibung) …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {/* P1 (Speisekarten): Filter nach Karte — Kategorien-Dropdown folgt der Auswahl. */}
          {cards.length > 0 && (
            <select
              className="input max-w-[220px]"
              value={cardFilter}
              onChange={(e) => {
                setCardFilter(e.target.value);
                setCategoryFilter("all");
              }}
            >
              <option value="all">Alle Karten</option>
              <option value="none">Ohne Karte</option>
              {cards.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          )}
          <select className="input max-w-[220px]" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="all">Alle Kategorien</option>
            <option value="none">Ohne Kategorie</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Archivierte zeigen
          </label>
          {/* CX-Audit 2026-07-17: sichtbarer, abwählbarer Chip für den ?source=-Deep-Link
              (Ziel des „Fertig"-Schritts im Speisekarten-Import). */}
          {sourceFilter && (
            <button
              type="button"
              onClick={clearSourceFilter}
              title="Filter entfernen"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-label-md text-[11.5px] font-semibold text-primary hover:bg-primary/20"
            >
              {sourceFilter === "ai_import" ? "Nur KI-Import" : `Quelle: ${sourceFilter}`}
              <Icon name="close" className="text-[14px]" />
            </button>
          )}
        </div>

        {error && <ErrorNote message={error} />}
        {formError && !itemForm && !catDrawerOpen && <ErrorNote message={formError} />}
        {loading ? (
          <LoadingRows />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="menu_book"
            title="Keine Positionen gefunden"
            hint={
              search || kindFilter !== "all" || categoryFilter !== "all"
                ? "Filter oder Suche anpassen."
                : "Legen Sie Ihre erste Katalog-Position an – oder importieren Sie eine Speisekarte."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr>
                  <th className="table-header">Position</th>
                  <th className="table-header">Kategorie</th>
                  <th className="table-header">Allergene</th>
                  <th className="table-header text-right">Preis</th>
                  <th className="table-header" />
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                {groupedRows.map((group) => (
                  <Fragment key={group.key}>
                    {group.label != null && (
                      /* Sichtbare Karten-Gruppierung: die Speisekarte IST die Gliederung des
                         Katalogs — nicht nur ein Filter. „Ohne Karte" bildet die letzte Gruppe. */
                      <tr className="bg-surface-container-low/70 dark:bg-surface-variant/10">
                        <td colSpan={5} className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-on-surface dark:text-inverse-on-surface">
                            <Icon name="menu_book" className="text-[16px] text-primary" /> {group.label}
                            <span className="font-body-sm text-body-sm normal-case tracking-normal text-on-surface-variant/70">
                              · {group.items.length} Position{group.items.length === 1 ? "" : "en"}
                            </span>
                          </span>
                        </td>
                      </tr>
                    )}
                    {group.items.map((it) => (
                  <tr
                    key={it.id}
                    className={`group cursor-pointer transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20 ${
                      it.archivedAt != null ? "opacity-55" : ""
                    }`}
                    onClick={() => openEdit(it)}
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-container text-on-secondary-container dark:bg-surface-variant dark:text-inverse-on-surface">
                          <Icon name={KIND_ICON[it.kind]} className="text-[18px]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-body-md text-body-md font-semibold text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface">
                              {it.name}
                            </span>
                            {it.archivedAt != null && (
                              <span className="rounded bg-surface-container-high px-1.5 py-0.5 font-label-md text-[10px] uppercase tracking-wide text-on-surface-variant dark:bg-surface-variant/30">
                                Archiviert
                              </span>
                            )}
                            {it.source === "ai_import" && (
                              /* CX-Audit 2026-07-17: Badge verlinkt zur Import-Seite (Herkunft nachvollziehen /
                                 nächste Karte importieren) — stopPropagation, da die Zeile den Edit-Drawer öffnet. */
                              <Link
                                to="/katalog/import"
                                onClick={(e) => e.stopPropagation()}
                                className="rounded bg-primary/10 px-1.5 py-0.5 font-label-md text-[10px] uppercase tracking-wide text-primary hover:bg-primary/20"
                                title="Per KI importiert — zur Import-Seite"
                              >
                                KI
                              </Link>
                            )}
                          </div>
                          {it.description && (
                            <div className="truncate max-w-[280px] font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                              {it.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {categoryName(it.categoryId)}
                    </td>
                    <td className="table-cell">
                      {it.allergens == null ? (
                        <span className="font-body-sm text-body-sm text-on-surface-variant/50" title="Allergene ungepflegt">
                          –
                        </span>
                      ) : it.allergens.length === 0 ? (
                        <span className="font-body-sm text-body-sm text-green-700 dark:text-green-300">keine</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {it.allergens.map((a) => (
                            <span
                              key={a}
                              className="inline-flex h-5 min-w-5 items-center justify-center rounded bg-surface-container-high px-1 font-label-md text-[11px] font-semibold text-on-surface-variant dark:bg-surface-variant/30"
                              title={LMIV.find((l) => l.code === a)?.label ?? a}
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="table-cell text-right">
                      <div className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                        {it.priceNote ? it.priceNote : money(it.priceCents)}
                      </div>
                      <div className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        {PRICING_UNIT_LABEL[it.pricingUnit]}
                      </div>
                    </td>
                    <td className="table-cell text-right">
                      <button
                        type="button"
                        className="btn-ghost !px-2 opacity-0 transition-opacity group-hover:opacity-100"
                        title={it.archivedAt != null ? "Wiederherstellen" : "Archivieren"}
                        onClick={(e) => {
                          e.stopPropagation();
                          void toggleArchive(it);
                        }}
                      >
                        <Icon name={it.archivedAt != null ? "unarchive" : "archive"} className="text-[18px]" />
                      </button>
                    </td>
                  </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Anlegen / Bearbeiten */}
      <Drawer
        open={itemForm != null}
        onClose={() => setItemForm(null)}
        title={itemForm?.id ? "Position bearbeiten" : "Position anlegen"}
      >
        {itemForm && (
          <form className="space-y-5" onSubmit={(e) => void handleSaveItem(e)}>
            {formError && <ErrorNote message={formError} />}

            <Field label="Bezeichnung *">
              <input
                required
                className="input"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Art">
                <select
                  className="input"
                  value={itemForm.kind}
                  onChange={(e) => setItemForm({ ...itemForm, kind: e.target.value as CatalogKind })}
                >
                  {KIND_ORDER.map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Kategorie">
                <select
                  className="input"
                  value={itemForm.categoryId}
                  onChange={(e) => setItemForm({ ...itemForm, categoryId: e.target.value })}
                >
                  <option value="">Ohne Kategorie</option>
                  {activeCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Preis (Brutto €)">
                <input
                  className="input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={itemForm.priceEuros}
                  onChange={(e) => setItemForm({ ...itemForm, priceEuros: sanitizePriceInput(e.target.value) })}
                />
              </Field>
              <Field label="Einheit">
                <select
                  className="input"
                  value={itemForm.pricingUnit}
                  onChange={(e) => setItemForm({ ...itemForm, pricingUnit: e.target.value as PricingUnit })}
                >
                  {(Object.keys(PRICING_UNIT_LABEL) as PricingUnit[]).map((u) => (
                    <option key={u} value={u}>
                      {PRICING_UNIT_LABEL[u]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="MwSt-Klasse">
              <select
                className="input"
                value={itemForm.vatClass}
                onChange={(e) => setItemForm({ ...itemForm, vatClass: e.target.value as VatClass })}
              >
                {(Object.keys(VAT_LABEL) as VatClass[]).map((v) => (
                  <option key={v} value={v}>
                    {VAT_LABEL[v]}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Preis-Hinweis (optional, rechnet nie)">
              <input
                className="input"
                placeholder='z. B. "ab 12,90 €"'
                value={itemForm.priceNote}
                onChange={(e) => setItemForm({ ...itemForm, priceNote: e.target.value })}
              />
            </Field>

            <Field label="Beschreibung">
              <textarea
                className="input min-h-[80px]"
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
              />
            </Field>

            {/* Allergene (LMIV) */}
            <div>
              <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                Allergene (LMIV)
              </span>
              <p className="mb-2 font-body-sm text-body-sm text-on-surface-variant/70">
                Nichts auswählen = <em>ungepflegt</em>. Bewusst „keine" via Häkchen unten.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {LMIV.map(({ code, label }) => {
                  const on = (itemForm.allergens ?? []).includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      title={label}
                      onClick={() => {
                        // Auswahl eines Allergens macht aus „ungepflegt" (null) einen gepflegten Stand.
                        const cur = itemForm.allergens ?? [];
                        setItemForm({
                          ...itemForm,
                          allergens: on ? cur.filter((a) => a !== code) : [...cur, code],
                        });
                      }}
                      className={`rounded-lg border px-2.5 py-1 font-label-md text-label-md transition-colors ${
                        on
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-outline-variant/50 text-on-surface-variant hover:border-primary/40 dark:border-outline/40 dark:text-secondary-fixed-dim"
                      }`}
                    >
                      <span className="font-bold">{code}</span> {label}
                    </button>
                  );
                })}
              </div>
              <label className="mt-2 flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                <input
                  type="checkbox"
                  // Tri-State: angehakt = [] (geprüft: keine), abgehakt = null (ungepflegt).
                  checked={itemForm.allergens != null && itemForm.allergens.length === 0}
                  onChange={(e) => setItemForm({ ...itemForm, allergens: e.target.checked ? [] : null })}
                />
                Geprüft: enthält keine der 14 Hauptallergene
              </label>
            </div>

            {/* Ernährung */}
            <div>
              <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                Ernährung
              </span>
              <div className="flex flex-wrap gap-1.5">
                {DIETARY.map(({ key, label }) => {
                  const on = !!itemForm.dietary[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setItemForm({ ...itemForm, dietary: { ...itemForm.dietary, [key]: !on } })}
                      className={`rounded-lg border px-2.5 py-1 font-label-md text-label-md transition-colors ${
                        on
                          ? "border-green-600 bg-green-600/10 text-green-700 dark:text-green-300"
                          : "border-outline-variant/50 text-on-surface-variant hover:border-green-500/40 dark:border-outline/40 dark:text-secondary-fixed-dim"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mehrsprachigkeit: Übersetzungen je Sprache (Angebots-Sprache steuert die Anzeige
                beim Kunden; Basissprache bleibt das Namensfeld oben). */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Übersetzungen
                </span>
                <select className="input !h-8 !w-auto !py-0.5 text-body-sm" value={editLang} onChange={(e) => setEditLang(e.target.value)}>
                  <option value="en">Englisch</option>
                  <option value="it">Italienisch</option>
                  <option value="fr">Französisch</option>
                  <option value="es">Spanisch</option>
                </select>
              </div>
              <div className="space-y-2">
                <input
                  className="input"
                  placeholder={`Name (${editLang.toUpperCase()}) – leer = keine Übersetzung`}
                  value={itemForm.translations[editLang]?.name ?? ""}
                  onChange={(e) =>
                    setItemForm({
                      ...itemForm,
                      translations: { ...itemForm.translations, [editLang]: { ...itemForm.translations[editLang], name: e.target.value } },
                    })
                  }
                />
                <input
                  className="input"
                  placeholder={`Beschreibung (${editLang.toUpperCase()}), optional`}
                  value={itemForm.translations[editLang]?.description ?? ""}
                  onChange={(e) =>
                    setItemForm({
                      ...itemForm,
                      translations: {
                        ...itemForm.translations,
                        [editLang]: { ...itemForm.translations[editLang], description: e.target.value || null },
                      },
                    })
                  }
                />
              </div>
            </div>

            {itemForm.id && (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-low px-3 py-2 font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
                <input
                  type="checkbox"
                  checked={itemForm.archived}
                  onChange={(e) => setItemForm({ ...itemForm, archived: e.target.checked })}
                />
                Archiviert (nicht mehr in neuen Angeboten wählbar)
              </label>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button type="button" className="btn-secondary" onClick={() => setItemForm(null)}>
                Abbrechen
              </button>
              <button type="submit" className="btn-primary" disabled={busy}>
                {busy ? "Speichert …" : itemForm.id ? "Speichern" : "Position anlegen"}
              </button>
            </div>
          </form>
        )}
      </Drawer>

      {/* Kategorie anlegen */}
      <Drawer open={catDrawerOpen} onClose={() => setCatDrawerOpen(false)} title="Kategorie anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreateCategory(e)}>
          {formError && catDrawerOpen && <ErrorNote message={formError} />}
          <Field label="Name *">
            <input name="name" required className="input" placeholder="z. B. Antipasti, Fingerfood, Getränke" />
          </Field>
          <Field label="Nur für Art (optional)">
            <select name="kindScope" className="input" defaultValue="">
              <option value="">Alle Arten</option>
              {KIND_ORDER.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
          {activeCategories.length > 0 && (
            <div className="rounded-lg border border-outline-variant/30 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
              <p className="mb-2 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Vorhandene Kategorien
              </p>
              <div className="flex flex-wrap gap-1.5">
                {activeCategories.map((c) => (
                  <span
                    key={c.id}
                    className="rounded-lg bg-surface-container-high px-2 py-1 font-label-md text-label-md text-on-surface-variant dark:bg-surface-variant/30"
                  >
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" className="btn-secondary" onClick={() => setCatDrawerOpen(false)}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Speichert …" : "Kategorie anlegen"}
            </button>
          </div>
        </form>
      </Drawer>
    </>
  );
  }
}

/* ------------------------------- Kleinteile -------------------------------- */
function FilterChip({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-label-md text-label-md transition-colors ${
        active
          ? "bg-primary text-on-primary"
          : "bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest dark:bg-surface-variant/30 dark:text-secondary-fixed-dim"
      }`}
    >
      {icon && <Icon name={icon} className="text-[16px]" />}
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
        {label}
      </span>
      {children}
    </label>
  );
}

function errorText(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes("duplicate_name")) return "Es gibt bereits eine aktive Position mit diesem Namen in dieser Kategorie.";
  if (msg.includes("invalid_category")) return "Die gewählte Kategorie ist ungültig.";
  if (msg.includes("forbidden_role")) return "Nur Inhaber/Admin dürfen das.";
  return msg;
}
